import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { clampQuizFields, validateQuizList } from "./quizHelpers";
import QuizTab from "./QuizTab";
import GameImageTab from "./GameImageTab";
import StageRulesEditor, {
  normalizeRulesFromTheme,
  validateStageRules,
} from "./StageRulesEditor";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

const GAME_TABS = [
  { id: "quiz", label: "Quiz" },
  { id: "game_image", label: "Game Image" },
];

const DEFAULT_GAME_TAB = GAME_TABS[0].id;
const GAME_TAB_PARAM = "tab";

function isThemeFlagOn(val, defaultOn = false) {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
}

function getGameTabFromSearch(params) {
  const tab = params.get(GAME_TAB_PARAM);
  return GAME_TABS.some((t) => t.id === tab) ? tab : DEFAULT_GAME_TAB;
}

let pairIdCounter = 0;
function newPairId(prefix = "pair") {
  pairIdCounter += 1;
  return `${prefix}-${pairIdCounter}`;
}

function normalizeQuizFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => ({
    id: newPairId("quiz"),
    ...clampQuizFields(item),
  }));
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const quizEnabled = isThemeFlagOn(data?.quiz_runner, true);
  const activeTab = getGameTabFromSearch(searchParams);
  const [isSaving, setIsSaving] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizErrorMessage, setQuizErrorMessage] = useState("");
  const [quizSuccessMessage, setQuizSuccessMessage] = useState("");
  const [quizRules, setQuizRules] = useState([""]);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setQuizQuestions(normalizeQuizFromTheme(data.quiz));
    setQuizRules(normalizeRulesFromTheme(data.quiz_rules));
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (!shouldAutoStartThemeUpdateTour()) return;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  const handleSaveQuizRules = async () => {
    const rulesCheck = validateStageRules(quizRules, "Quiz");
    if (!rulesCheck.ok) {
      setQuizErrorMessage(rulesCheck.message);
      setQuizSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz_rules: rulesCheck.rules,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Quiz rules saved to theme.", "success");
      setQuizSuccessMessage("Rules saved to theme.");
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save quiz rules.");
    } finally {
      setIsSaving(false);
    }
  };

  const persistQuizQuestions = async (nextQuestions) => {
    const validation = validateQuizList(
      nextQuestions.map(({ id, ...rest }) => rest)
    );
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz: validation.data,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setQuizQuestions(
        nextQuestions.map((q, i) => ({
          id: q.id || newPairId("quiz"),
          ...validation.data[i],
        }))
      );
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save quiz data.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const clearTabMessages = () => {
    setQuizErrorMessage("");
    setQuizSuccessMessage("");
  };

  const changeTab = useCallback(
    (tabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === DEFAULT_GAME_TAB) {
            next.delete(GAME_TAB_PARAM);
          } else {
            next.set(GAME_TAB_PARAM, tabId);
          }
          return next;
        },
        { replace: true }
      );
      clearTabMessages();
    },
    [setSearchParams]
  );

  const handleTourStepChange = useCallback(
    (step) => {
      if (!step?.tab) return;
      changeTab(step.tab);
    },
    [changeTab]
  );

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide" data-tour="tour-gc-header">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">
          Manage questions and content for each mini-game in this theme.
        </p>
      </header>

      {GAME_TABS.length > 1 ? (
      <nav className="themeupdate-tabs" aria-label="Game tabs" data-tour="tour-gc-tabs">
        {GAME_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`themeupdate-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => changeTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      ) : null}

      {activeTab === "quiz" && (
        <>
          <StageRulesEditor
            stageLabel="Stage 3"
            rules={quizRules}
            setRules={setQuizRules}
            onSave={handleSaveQuizRules}
            isSaving={isSaving}
            saveLabel="Save rules"
          />
          {quizEnabled ? (
            <QuizTab
              questions={quizQuestions}
              setQuestions={setQuizQuestions}
              isSaving={isSaving}
              onPersist={persistQuizQuestions}
              errorMessage={quizErrorMessage}
              successMessage={quizSuccessMessage}
              setErrorMessage={setQuizErrorMessage}
              setSuccessMessage={setQuizSuccessMessage}
              newQuestionId={() => newPairId("quiz")}
            />
          ) : null}
        </>
      )}

      {activeTab === "game_image" && (
        <GameImageTab
          themeData={data}
          adminToken={adminToken}
          currentTheme={currentTheme}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          onSaved={() =>
            dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap()
          }
        />
      )}

      <ThemeUpdateFeatureTour
        open={tourOpen}
        onClose={closeTour}
        onStepChange={handleTourStepChange}
        quizEnabled={quizEnabled}
      />
    </div>
  );
};

export default ThemeUpdate;
